import { IMAGE_PRESETS } from '@/lib/image-presets'
import { getImageUrl } from '@/lib/utils'
import { extractRelativeMediaPath } from '@/lib/sync-media-caption'

export type LightboxItem = {
  src: string
  alt?: string
  thumb?: string
  title?: string
  description?: string
}

export function findLightboxIndex(items: LightboxItem[], src?: string): number {
  if (!src || items.length === 0) return 0
  const resolved = resolveLightboxSrc(src)
  const index = items.findIndex(
    (item) => item.src === src || item.src === resolved || resolveLightboxSrc(item.src) === resolved,
  )
  return index >= 0 ? index : 0
}

const SKIP_CLASS = new Set(['related-news-card__image', 'content-card-image'])

const SKIP_CLOSEST =
  '.related-news-card, .content-card, .ad-slot, [data-ad-root], .ad-root, .article-author'

/** Sadece görselin (veya <picture> sarmalayıcısının) kendisine tıklanınca aç */
export function isDirectZoomableImageClick(target: EventTarget | null, root: ParentNode): HTMLImageElement | null {
  if (!(target instanceof Element) || !root.contains(target)) return null
  if (target.closest('figcaption, .related-news-card, .content-card, .ad-slot, [data-ad-root], .ad-root, .article-author')) {
    return null
  }

  let img: HTMLImageElement | null = null
  if (target instanceof HTMLImageElement) {
    img = target
  } else if (target.tagName === 'PICTURE' || target.tagName === 'SOURCE') {
    const nested = target.closest('picture')?.querySelector('img')
    img = nested instanceof HTMLImageElement ? nested : null
  }

  if (!img || !root.contains(img) || !isZoomableArticleImage(img)) return null
  return img
}

export function isZoomableArticleImage(img: HTMLImageElement): boolean {
  if (!(img.currentSrc || img.src)) return false
  if (img.closest(SKIP_CLOSEST)) return false
  for (const className of img.classList) {
    if (SKIP_CLASS.has(className)) return false
  }
  if (img.naturalWidth > 0 && img.naturalWidth < 80) return false
  return true
}

/** Yazı içi görseller sıkça <a href="foto.jpg"> ile sarılı — tarayıcı dosyayı açmasın */
export function isImageFileHref(href: string | null | undefined): boolean {
  const value = String(href || '').trim()
  if (!value || value.startsWith('#') || value.startsWith('mailto:')) return false
  if (/\.(jpe?g|png|webp|gif|avif|bmp)(?:\?|#|$)/i.test(value)) return true
  return Boolean(extractRelativeMediaPath(value))
}

export function resolveLightboxSrc(srcOrImg: string | HTMLImageElement): string {
  const src = typeof srcOrImg === 'string' ? srcOrImg : srcOrImg.currentSrc || srcOrImg.src
  if (!src) return ''
  const relative = extractRelativeMediaPath(src)
  if (relative) return getImageUrl(relative) || src
  return src
}

export function collectZoomableImages(root: ParentNode): LightboxItem[] {
  return Array.from(root.querySelectorAll('img'))
    .filter(
      (el): el is HTMLImageElement =>
        el instanceof HTMLImageElement && isZoomableArticleImage(el),
    )
    .map((img) => {
      const src = resolveLightboxSrc(img)
      const relative = extractRelativeMediaPath(img.currentSrc || img.src)
      const figcaption = img.closest('figure')?.querySelector('figcaption')?.textContent?.trim()
      const title = img.title?.trim() || ''
      const description = figcaption || img.alt?.trim() || ''
      return {
        src,
        alt: title || description,
        title: title || undefined,
        description: description && description !== title ? description : figcaption || undefined,
        thumb: relative ? getImageUrl(relative, IMAGE_PRESETS.thumb) || src : src,
      }
    })
    .filter((item) => item.src)
}

export function dedupeLightboxItems(items: LightboxItem[]): LightboxItem[] {
  const seen = new Set<string>()
  const out: LightboxItem[] = []
  for (const item of items) {
    const key = extractRelativeMediaPath(item.src) || item.src
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}
